import { useIsMutating } from "@tanstack/react-query";
import cn from "classnames";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { Button, Loader, Modal } from "ui";

import {
  AiLesson,
  AiLessonReplayResult,
  AiLessonReplayRun,
  AiLessonReplayVerdict,
  EAiLessonStatus
} from "../../aiAgents.types";
import {
  lessonReplayMutationKey,
  useGetLesson,
  useReplayLesson,
  useSetReplayVerdict
} from "../../apis";
import { AI_LESSON_REPLAY } from "../../constants/lessons";
import { parseLessonApiError } from "../../helpers/lessons";
import {
  formatUsd,
  isReplayInProgressError,
  lessonCanReplay,
  replayApproximateReasonKeys,
  replayCanJudge,
  replayErrorKey,
  replayEstimateUsd,
  replayIsOutdated,
  replayOutcomeKey,
  replayState,
  replayVerdictToastKey,
  replayVisualLabelKeys
} from "../../helpers/replay";
import { ReplayToolTrace } from "../ReplayToolTrace";

import styles from "./LessonReplayModal.module.scss";

interface Props {
  assistantId: string;
  // Open when set. The list's copy, without the runs: the window reads the
  // full lesson itself.
  lesson: AiLesson | null;
  canManage: boolean;
  onClose: () => void;
  // R1 answered a route 404 (a backend without the replay): the tab hides
  // the action for the rest of the visit.
  onUnavailable?: () => void;
}

const formatDate = (iso: string): string => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString(undefined, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  });
};

const ReplyText = ({ text }: { text: string | null }) => {
  const { t } = useTranslation();
  return text ? (
    <p className={styles.cardText}>{text}</p>
  ) : (
    <p className={cn(styles.cardText, styles.cardTextEmpty)}>
      {t("aiReplay.noText")}
    </p>
  );
};

// "Before": what the customer really got.
const BeforeCard = ({ before }: { before: AiLessonReplayResult["before"] }) => {
  const { t } = useTranslation();
  const cards = before?.cards ?? [];
  return (
    <div className={cn(styles.card, styles.cardBefore)}>
      <div className={styles.cardTitle}>{t("aiReplay.before")}</div>
      <div className={styles.outcome}>
        {t(replayOutcomeKey(before?.outcome))}
      </div>
      <ReplyText text={before?.text ?? null} />
      {cards.length > 0 ? (
        <div className={styles.cardLine}>
          {t("aiReplay.sent", { items: cards.join(", ") })}
        </div>
      ) : null}
    </div>
  );
};

// One replayed run, with the lesson or without it (the control).
const RunCard = ({
  title,
  run,
  withLesson
}: {
  title: string;
  run: AiLessonReplayRun;
  withLesson?: boolean;
}) => {
  const { t } = useTranslation();
  const visuals = replayVisualLabelKeys(run).map((key) => t(key));
  const repairs = run.repairs ?? [];
  const tools = run.toolTrace ?? [];

  return (
    <div className={cn(styles.card, { [styles.cardLesson]: withLesson })}>
      <div className={styles.cardTitle}>{title}</div>
      <div className={cn(styles.outcome, styles[`outcome_${run.outcome}`])}>
        {t(replayOutcomeKey(run.outcome))}
      </div>
      <ReplyText text={run.text} />
      {visuals.length > 0 ? (
        <div className={styles.cardLine}>
          {t("aiReplay.sent", { items: visuals.join(", ") })}
        </div>
      ) : null}
      {repairs.length > 0 ? (
        <div className={styles.cardLine}>
          <span className={styles.cardLineLabel}>{t("aiTurn.repairs")}</span>{" "}
          {repairs.join(", ")}
        </div>
      ) : null}
      {tools.length > 0 || run.traceTruncated ? (
        // Collapsed: the replies are what the admin compares first.
        <details className={styles.tools}>
          <summary className={styles.toolsSummary}>
            {t("aiReplay.tools", { count: tools.length })}
          </summary>
          <div className={styles.toolsList}>
            {tools.map((tool, index) => (
              <ReplayToolTrace
                key={`${tool.round}-${tool.toolName}-${index}`}
                tool={tool}
              />
            ))}
            {run.traceTruncated ? (
              <div className={styles.muted}>{t("aiTurn.trace.truncated")}</div>
            ) : null}
          </div>
        </details>
      ) : null}
      <div className={styles.cardFoot}>
        {t("aiReplay.usage", { rounds: run.rounds })}
      </div>
    </div>
  );
};

const ReplayResult = ({
  lesson,
  result,
  canManage,
  locale,
  verdictPending,
  onVerdict
}: {
  lesson: AiLesson;
  result: AiLessonReplayResult;
  canManage: boolean;
  locale: string;
  verdictPending: boolean;
  onVerdict: (verdict: AiLessonReplayVerdict) => void;
}) => {
  const { t } = useTranslation();
  const reasonKeys = replayApproximateReasonKeys(result);
  const canJudge = replayCanJudge(lesson, canManage, Date.now());
  const verdictLabel = (verdict: AiLessonReplayVerdict) =>
    t(
      verdict === "passed" ? "aiReplay.verdictPassed" : "aiReplay.verdictFailed"
    );

  return (
    <>
      <div className={styles.warnings}>
        <div className={styles.notice}>{t("aiReplay.todayWarning")}</div>
        {result.approximate ? (
          <div className={styles.warning}>
            <div>{t("aiReplay.approximate")}</div>
            {reasonKeys.length > 0 ? (
              <ul className={styles.reasons}>
                {reasonKeys.map((key) => (
                  <li key={key}>{t(key)}</li>
                ))}
              </ul>
            ) : null}
          </div>
        ) : null}
        {result.systemHashChanged === true ? (
          <div className={styles.warning}>{t("aiReplay.rulesChanged")}</div>
        ) : null}
        {result.lessonMatchesTurn === false ? (
          <div className={styles.warning}>{t("aiReplay.notMatching")}</div>
        ) : null}
        {replayIsOutdated(lesson) ? (
          <div className={styles.warning}>{t("aiReplay.outdated")}</div>
        ) : null}
      </div>

      {/* Before | without the lesson | with it, side by side; one column on a
          narrow screen. "Before" stays in view while the runs scroll. */}
      <div className={styles.compare}>
        <div className={styles.beforeColumn}>
          <BeforeCard before={result.before} />
        </div>
        <div className={styles.runsColumn}>
          {result.runs.map((run, index) => (
            <section key={run.index} className={styles.run}>
              <div className={styles.runTitle}>
                {t("aiReplay.runTitle", { n: run.index + 1 })}
              </div>
              {index === 0 ? (
                <div className={styles.muted}>{t("aiReplay.controlHelp")}</div>
              ) : null}
              <div className={styles.pair}>
                <RunCard title={t("aiReplay.control")} run={run.control} />
                <RunCard
                  title={t("aiReplay.withLesson")}
                  run={run.withLesson}
                  withLesson
                />
              </div>
            </section>
          ))}
        </div>
      </div>

      <div className={styles.cost}>
        {t("aiReplay.cost", {
          cost: formatUsd(result.costUsdEstimate, locale),
          rounds: result.totalRounds,
          perRound: formatUsd(AI_LESSON_REPLAY.COST_USD_PER_ROUND, locale)
        })}
      </div>

      {canJudge ? (
        <section className={styles.verdict}>
          <div className={styles.verdictQuestion}>
            {t("aiReplay.verdictQuestion")}
          </div>
          <div className={styles.muted}>{t("aiReplay.verdictHelp")}</div>
          <div className={styles.verdictButtons}>
            {(["passed", "failed"] as const).map((verdict) => (
              <button
                key={verdict}
                type="button"
                className={cn(
                  styles.verdictButton,
                  styles[`verdict_${verdict}`],
                  {
                    [styles.verdictChosen]: result.verdict === verdict
                  }
                )}
                aria-pressed={result.verdict === verdict}
                disabled={verdictPending}
                onClick={() => onVerdict(verdict)}
              >
                {verdictLabel(verdict)}
              </button>
            ))}
          </div>
        </section>
      ) : null}
      {result.judgedAt && result.verdict ? (
        <div className={styles.muted}>
          {t("aiReplay.judged", {
            date: formatDate(result.judgedAt),
            verdict: verdictLabel(result.verdict)
          })}
        </div>
      ) : null}
    </>
  );
};

const Replay = ({
  assistantId,
  lesson: listLesson,
  canManage,
  onClose,
  onUnavailable
}: Props & { lesson: AiLesson }) => {
  const { t, i18n } = useTranslation();
  const lessonId = listLesson.id;
  const [runs, setRuns] = useState<number>(AI_LESSON_REPLAY.DEFAULT_RUNS);
  const [unavailable, setUnavailable] = useState(false);
  const [poll, setPoll] = useState(false);

  const replayLesson = useReplayLesson(assistantId);
  const setReplayVerdict = useSetReplayVerdict(assistantId);
  // Also true when this window was closed and opened again while this
  // browser still waits for R1: its answer then lands in the cache.
  const pendingInCache =
    useIsMutating({
      mutationKey: lessonReplayMutationKey(assistantId),
      predicate: (mutation) =>
        (mutation.state.variables as { lessonId?: string } | undefined)
          ?.lessonId === lessonId
    }) > 0;
  const ownPending = pendingInCache || replayLesson.isLoading;

  const lessonQuery = useGetLesson(assistantId, lessonId, { poll });
  const full = lessonQuery.lesson;
  const current = full ?? listLesson;
  const result = current.lastReplayResult;
  const state = replayState(result, Date.now());
  // The runs come from L2 only (the list leaves them out).
  const fullResult =
    full?.lastReplayResult && !full.lastReplayResult.runsOmitted
      ? full.lastReplayResult
      : null;
  const running = ownPending || state === "running";

  // A replay started elsewhere (another tab or admin, or before a reload):
  // ask for the result until it lands or reads as interrupted.
  const shouldPoll = !ownPending && state === "running";
  useEffect(() => {
    setPoll(shouldPoll);
  }, [shouldPoll]);

  const markUnavailable = () => {
    setUnavailable(true);
    onUnavailable?.();
  };

  const showErrors = (error: unknown, fallbackKey: string) => {
    const details = parseLessonApiError(error);
    if (details.endpointMissing) {
      markUnavailable();
      return;
    }
    (details.messages.length > 0 ? details.messages : [fallbackKey]).forEach(
      (message) => toast.error(t(message))
    );
  };

  // The end toast and the cache update are in useReplayLesson, so they still
  // happen if the window is closed during the run.
  const handleStart = () => {
    if (running) return;
    replayLesson.mutate(
      { lessonId, runs },
      {
        onError: (error) => {
          if (!isReplayInProgressError(error)) {
            showErrors(error, "aiLesson.toast.replayFailed");
          }
          // In progress, or cut before the answer: the result may still come.
          void lessonQuery.refetch();
        }
      }
    );
  };

  const handleVerdict = (verdict: AiLessonReplayVerdict) => {
    if (setReplayVerdict.isLoading) return;
    setReplayVerdict.mutate(
      { lessonId, verdict },
      {
        // A live lesson stays live: no "can now be activated" for it.
        onSuccess: (saved) =>
          toast.success(t(replayVerdictToastKey(verdict, saved?.status))),
        onError: (error) => {
          showErrors(error, "aiReplay.verdictSaveFailed");
          void lessonQuery.refetch();
        }
      }
    );
  };

  const estimate = replayEstimateUsd(runs);
  const locale = i18n.language;

  const renderControls = () => {
    if (unavailable) {
      return <div className={styles.notice}>{t("aiReplay.unavailable")}</div>;
    }
    if (!canManage) {
      return <div className={styles.notice}>{t("aiLesson.noPermission")}</div>;
    }
    if (current.status === EAiLessonStatus.Archived) return null;
    if (!lessonCanReplay(current, canManage)) {
      return (
        <div className={styles.notice}>{t("aiLesson.replayNoSource")}</div>
      );
    }
    return (
      <section className={styles.controls}>
        <div className={styles.controlsRow}>
          <label className={styles.field}>
            <span className={styles.label}>{t("aiReplay.runs")}</span>
            <select
              className={styles.select}
              value={runs}
              disabled={running}
              onChange={(e) => setRuns(Number(e.target.value))}
            >
              {AI_LESSON_REPLAY.RUNS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </label>
          <Button
            type="button"
            variant="primary"
            loading={ownPending}
            disabled={running}
            onClick={handleStart}
          >
            {t(result ? "aiReplay.again" : "aiReplay.start")}
          </Button>
        </div>
        <div className={styles.muted}>{t("aiReplay.runsHelp")}</div>
        <div className={styles.estimate}>
          {t("aiReplay.estimate", {
            expected: formatUsd(estimate.expected, locale),
            max: formatUsd(estimate.max, locale)
          })}
        </div>
      </section>
    );
  };

  const renderState = () => {
    if (ownPending || state === "running") {
      return (
        <div className={styles.running}>
          <Loader size={22} />
          <span>
            {t(ownPending ? "aiReplay.running" : "aiReplay.runningElsewhere")}
          </span>
        </div>
      );
    }
    switch (state) {
      case "interrupted":
        return (
          <div className={styles.warning}>{t("aiReplay.interrupted")}</div>
        );
      case "error":
        return (
          <div className={styles.errorBox}>
            {t(replayErrorKey(result?.error))}
          </div>
        );
      case "done":
        if (full && fullResult) {
          return (
            <ReplayResult
              lesson={full}
              result={fullResult}
              canManage={canManage && !unavailable}
              locale={locale}
              verdictPending={setReplayVerdict.isLoading}
              onVerdict={handleVerdict}
            />
          );
        }
        return lessonQuery.isError ? (
          <div className={styles.notice}>{t("aiReplay.loadFailed")}</div>
        ) : (
          <div className={styles.loaderWrap}>
            <Loader />
          </div>
        );
      default:
        return lessonQuery.isError && !full ? (
          <div className={styles.notice}>{t("aiReplay.loadFailed")}</div>
        ) : null;
    }
  };

  return (
    <Modal
      isOpen
      title="aiReplay.title"
      onClose={onClose}
      contentClassName={styles.content}
      footer={
        <div className={styles.footer}>
          <Button type="button" variant="default" block onClick={onClose}>
            {t("aiLesson.close")}
          </Button>
        </div>
      }
    >
      <div className={styles.body}>
        <p className={styles.intro}>{t("aiReplay.intro")}</p>

        <section className={styles.guidanceBlock}>
          <div className={styles.lessonTitle}>{current.title}</div>
          <div className={styles.label}>
            {t("aiReplay.guidance", { version: current.version })}
          </div>
          <p className={styles.guidance}>{current.guidance}</p>
        </section>

        {renderControls()}
        {renderState()}
      </div>
    </Modal>
  );
};

// Replay before activation: the lesson's source turn is replayed with the
// lesson and without it (the control), 1 to 3 times; nothing reaches the
// customer. The admin compares with the reply really sent and says "Fixed"
// or "Not fixed"; "Fixed" is what lets a non-Super admin activate it.
// Everyone can read a result; starting a replay and judging it need the
// "Manage AI lessons" permission (the backend checks it again).
const LessonReplayModal = ({ lesson, ...props }: Props) =>
  lesson ? <Replay key={lesson.id} lesson={lesson} {...props} /> : null;

export default LessonReplayModal;
